import Config from '@lib/config';
import { Order } from '@lib/entities/Order';

/**
 * StackAdapt conversion pixels for Optimal's programmatic campaign (JSAD-7304).
 *
 * Optimal supplied two vendor snippets: a queueing stub for `window.saq` plus an async load of
 * `events.js`, followed by `saq('conv', <pixel id>, { revenue })`. This module is that snippet
 * in TypeScript, injected lazily on the first conversion instead of in every page's <head>, so
 * a visitor who never starts an order never downloads it. Calls made before the script arrives
 * sit in `saq.queue` and flush when it loads — the vendor's own mechanism.
 *
 * Pixel ids come from Doppler (`EXPO_PUBLIC_STACKADAPT_*_PIXEL_ID`) and are set on prd only:
 * the nightly e2e places real orders on staging and those must never count as Optimal
 * conversions. Without an id every call is a no-op. Nothing here may throw into a click or
 * into checkout, hence the try/catch around the vendor call.
 */

type SaqFn = ((...args: unknown[]) => void) & {
    callMethod?: (...args: unknown[]) => void;
    queue?: unknown[];
    push?: SaqFn;
    loaded?: boolean;
    version?: string;
};

declare global {
    interface Window {
        saq?: SaqFn;
        _saq?: SaqFn;
    }
}

export const STACKADAPT_EVENTS_SRC = 'https://tags.srv.stackadapt.com/events.js';

/**
 * Installs the `saq` queueing stub and injects events.js. Mirrors the vendor loader, including
 * its `if (s.saq) return;` guard: once `window.saq` exists (ours or an SDK already on the page)
 * nothing is re-created or re-injected. Returns whether `window.saq` is available.
 */
export function loadStackAdapt(): boolean {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return false;
    }
    if (window.saq) {
        return true;
    }

    const saq: SaqFn = (...args: unknown[]) => {
        if (saq.callMethod) {
            saq.callMethod(...args);
        } else {
            saq.queue?.push(args);
        }
    };
    saq.queue = [];
    saq.push = saq;
    saq.loaded = true;
    saq.version = '1.0';
    window.saq = saq;
    if (!window._saq) {
        window._saq = saq;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = STACKADAPT_EVENTS_SRC;
    const first = document.getElementsByTagName('script')[0];
    if (first?.parentNode) {
        first.parentNode.insertBefore(script, first);
    } else {
        document.head.appendChild(script);
    }
    return true;
}

/**
 * Fires one StackAdapt conversion. No-op without a pixel id (non-production config) or outside
 * a browser. `data` is omitted from the call when absent, exactly like the Order Now snippet.
 */
export function trackStackAdaptConversion(
    pixelId: string | undefined,
    data?: Record<string, string>
): void {
    if (!pixelId) {
        return;
    }
    try {
        if (!loadStackAdapt()) {
            return;
        }
        if (data) {
            window.saq?.('conv', pixelId, data);
        } else {
            window.saq?.('conv', pixelId);
        }
    } catch (e) {
        console.error('[StackAdapt] conversion failed:', e);
    }
}

/** "Order Now" pixel — every order-intent click on the ordering site. */
export function trackStackAdaptOrderNow(): void {
    trackStackAdaptConversion(Config.stackadaptOrderNowPixelId);
}

/**
 * Purchase pixel — once per placed order, from the checkout success path. `revenue` is the
 * total charged (tax, tip and fees included), formatted like Rokt's `amount`; `order_id` lets
 * Optimal dedupe. A non-numeric total still reports the conversion, without revenue.
 */
export function trackStackAdaptPurchase(order: Pick<Order, 'order_id' | 'total'>): void {
    const total = Number(order.total);
    trackStackAdaptConversion(Config.stackadaptPurchasePixelId, {
        ...(Number.isFinite(total) ? { revenue: total.toFixed(2) } : {}),
        order_id: String(order.order_id)
    });
}
